import { createSlice, createAction } from "@reduxjs/toolkit";
import { RootState } from "store/store";
import {
	Purchase,
	PurchaseQueryParams,
	DataUpdatePurchase,
	DataCreatePurchase,
} from "../types";
import { ApiResponse } from "types";
import { ConflictBookingCar } from "features/appointment/types";

interface PurchaseState {
	purchaseList: Array<Purchase>;
	purchaseTotal: number;
	loading: boolean;
	purchaseDetail: Purchase | null;
}

const initialState: PurchaseState = {
	loading: false,
	purchaseList: [],
	purchaseTotal: 0,
	purchaseDetail: null,
};

export const purchaseSlice = createSlice({
	name: "purchase",
	initialState,
	reducers: {
		setListPurchase: (state, action) => {
			state.purchaseList = action.payload.data;
			state.purchaseTotal = action.payload.total;
		},
		setDetailPurchase: (state, action) => {
			state.purchaseDetail = action.payload.data;
		},
		setDetailPurchaseCars: (state, action) => {
			if (state.purchaseDetail?.purchaseCars) {
				state.purchaseDetail.purchaseCars = action.payload;
			}
		},
		setDetailPurchaseImportCars: (state, action) => {
			if (state.purchaseDetail?.purchaseImportCars) {
				state.purchaseDetail.purchaseImportCars = action.payload;
			}
		},
		resetDetailPurchase: state => {
			state.purchaseDetail = null;
		},
		setLoadingPurchase: (state, action) => {
			state.loading = action.payload;
		},
	},
});

export const selectPurchaseList = (state: RootState) =>
	state.purchase.purchaseList;
export const selectPurchaseTotal = (state: RootState) =>
	state.purchase.purchaseTotal;
export const selectPurchaseDetail = (state: RootState) =>
	state.purchase.purchaseDetail;
export const selectLoadingPurchase = (state: RootState) =>
	state.purchase.loading;

export const {
	setListPurchase,
	setDetailPurchase,
	resetDetailPurchase,
	setLoadingPurchase,
	setDetailPurchaseCars,
	setDetailPurchaseImportCars
} = purchaseSlice.actions;

export interface GetListPurchaseAsyncPayload {
	query: PurchaseQueryParams;
	callback?: () => void;
}
export const getListPurchaseAsync = createAction(
	"purchase/getListPurchaseAsync",
	(payload: GetListPurchaseAsyncPayload) => ({
		payload: payload,
	}),
);

// Get detail
export interface GetDetailPurchaseAsyncPayload {
	id: string;
	isImportCar?: boolean
	query: {
		language: string;
	};
	onSuccess?: (purchase: Purchase) => void;
}
export const getDetailPurchaseAsync = createAction(
	"purchase/getDetailPurchaseAsync",
	(payload: GetDetailPurchaseAsyncPayload) => ({
		payload,
	}),
);

// Update
export interface UpdatePurchaseAsyncPayload {
	data: DataUpdatePurchase;
	id: string;
	onSuccess: () => void;
}
export const updatePurchaseAsync = createAction(
	"purchase/updatePurchaseAsync",
	(payload: UpdatePurchaseAsyncPayload) => ({
		payload,
	}),
);

export interface CreatePurchaseAsyncPayload {
	data: DataCreatePurchase;
	onSuccess: () => void;
	onFailure?: (data: ApiResponse<ConflictBookingCar[]>) => void;
}
export const createPurchaseAsync = createAction(
	"purchase/createPurchaseAsync",
	(payload: CreatePurchaseAsyncPayload) => ({
		payload,
	}),
);

export interface ApplyVoucherCodePurchaseAsyncPayload {
	id?: string;
	voucherCode?: string;
	onSuccess?: () => void;
	onFailure?: (error: any) => void;
}

export const applyVoucherCodePurchaseAsyncAsync = createAction(
	"purchase/applyVoucherCodePurchaseAsyncAsync",
	(payload: ApplyVoucherCodePurchaseAsyncPayload) => ({
		payload,
	}),
);

export interface DeleteVoucherCodePurchaseAsyncPayload {
	id?: string;
	voucherCode?: string;
	onSuccess?: () => void;
	onFailure?: (error: any) => void;
}

export const deleteVoucherCodePurchaseAsyncAsync = createAction(
	"purchase/deleteVoucherCodePurchaseAsyncAsync",
	(payload: DeleteVoucherCodePurchaseAsyncPayload) => ({
		payload,
	}),
);

// add a service
export interface AddServiceAsyncPayload {
	data: {
		purchaseCars: {
			id: number;
			services: number[];
		}[];
	};
	purchaseOrderId: string | number;
	getResult: (arg: { success: boolean }) => void;
	isImportCar?: boolean
}
export const addServiceAsync = createAction(
	"purchase/addServiceAsync",
	(payload: AddServiceAsyncPayload) => ({
		payload,
	}),
);

// delete a service
export interface DeleteServiceAsyncPayload {
	purchaseOrderId: string | number;
	serviceCarId: string | number;
	getResult: (arg: { success: boolean }) => void;
	isImportCar?: boolean
}
export const deleteServiceAsync = createAction(
	"purchase/deleteServiceAsync",
	(payload: DeleteServiceAsyncPayload) => ({
		payload,
	}),
);

export default purchaseSlice.reducer;
